import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeSlash, CheckCircle, ArrowLeft, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

function strengthOf(pw) {
  let s = 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return s;
}
const LABEL = ["Too weak", "Weak", "Fair", "Good", "Strong", "Excellent"];
const COLOR = ["bg-rose-500", "bg-rose-500", "bg-amber-500", "bg-amber-500", "bg-emerald-500", "bg-emerald-600"];

export default function ResetPassword() {
  const [params] = useSearchParams();
  const email = params.get("email") || "";
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState({ new_password: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const strength = strengthOf(pw.new_password);

  const canSubmit = pw.new_password.length >= 8 &&
                    pw.new_password === pw.confirm && email && token;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post("/auth/reset-password",
        { email, token, new_password: pw.new_password });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  if (!email || !token) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-8">
        <div className="max-w-md text-center" data-testid="reset-invalid">
          <Warning size={32} className="mx-auto text-rose-600" />
          <h2 className="font-heading text-2xl mt-4">Invalid reset link</h2>
          <p className="text-sm text-zinc-500 mt-2">
            The reset link is missing required information. Please request a fresh one.
          </p>
          <Link to="/forgot-password">
            <Button className="mt-6 rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white">Request new link</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:flex bg-zinc-950 text-zinc-100 p-12 flex-col justify-between">
        <div>
          <div className="font-heading text-2xl tracking-tight">PARAM SKILLS</div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-400 mt-1">Training (I) Pvt. Ltd.</div>
        </div>
        <div>
          <h1 className="font-heading text-5xl leading-[0.95] tracking-tight">Pick a new,<br />stronger<br />password.</h1>
          <p className="mt-6 text-zinc-300 max-w-md text-sm leading-relaxed">
            Minimum 8 characters. Aim for 12+ with a mix of upper- and lower-case letters, numbers and symbols.
          </p>
        </div>
        <div className="text-xs text-zinc-500">© 2026 Param Skills.</div>
      </div>

      <div className="bg-white flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md" data-testid="reset-password-root">
          <Link to="/login" className="text-xs uppercase tracking-widest text-zinc-500 hover:text-black flex items-center gap-1">
            <ArrowLeft size={12} /> Back to sign in
          </Link>
          <div className="text-xs tracking-[0.3em] uppercase text-zinc-500 mt-6">Set new password</div>
          <h2 className="font-heading text-3xl mt-2 tracking-tight">Choose your new password</h2>
          <p className="text-sm text-zinc-500 mt-1">Resetting for <span className="font-mono text-zinc-900">{email}</span></p>

          {done ? (
            <div className="mt-8 border-l-2 border-emerald-600 bg-emerald-50 pl-4 py-4" data-testid="reset-done">
              <div className="font-medium text-emerald-900 flex items-center gap-2"><CheckCircle weight="fill" /> Password updated</div>
              <div className="text-sm text-emerald-800 mt-1">You can now sign in with your new password.</div>
              <Link to="/login">
                <Button className="mt-4 rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white h-10">Sign in →</Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-5">
              <div>
                <Label className="text-xs uppercase tracking-widest">New password</Label>
                <div className="relative mt-2">
                  <Input type={show ? "text" : "password"} value={pw.new_password}
                         onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
                         className="rounded-sm h-11 border-zinc-300 pr-10"
                         data-testid="reset-new-password"
                         autoComplete="new-password" required minLength={8} />
                  <button type="button" onClick={() => setShow(!show)}
                          className="absolute right-2 top-2.5 text-zinc-500 p-1">
                    {show ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {pw.new_password && (
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex-1 h-1 bg-zinc-100 flex gap-0.5">
                      {[0,1,2,3,4].map((i) => (
                        <div key={i} className={`h-full flex-1 ${i < strength ? COLOR[strength] : "bg-zinc-100"}`} />
                      ))}
                    </div>
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500">{LABEL[strength]}</span>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Confirm new password</Label>
                <Input type={show ? "text" : "password"} value={pw.confirm}
                       onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                       className="rounded-sm h-11 border-zinc-300 mt-2"
                       data-testid="reset-confirm-password"
                       autoComplete="new-password" required />
                {pw.confirm && pw.confirm !== pw.new_password && (
                  <div className="mt-1 text-xs text-rose-700">Passwords don't match</div>
                )}
              </div>
              <Button type="submit" disabled={!canSubmit || busy} data-testid="reset-submit"
                      className="w-full h-11 rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white font-medium tracking-wide">
                {busy ? "Updating…" : "Update password →"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
